/**
 * PullRequestDetailDialog — status transitions + reviews for a single pull
 * request (epic-6 §5 / GIT-4).
 *
 * Extracted from RepositoryDetailPage so both the repository detail tab and the
 * dedicated PullRequestsPage reuse the same UI over `repositoriesApi`.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useUpdatePullRequestMutation,
  useGetPullRequestReviewsQuery,
  useSubmitPullRequestReviewMutation,
} from '@/store/api/repositoriesApi';
import type { PullRequest, PullRequestReview, PullRequestStatus } from '@/types';

export const PR_STATUS_BADGE: Record<
  PullRequestStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  open: 'default',
  reviewing: 'secondary',
  merged: 'outline',
  closed: 'destructive',
};

export function PullRequestDetailDialog({
  repoId,
  pr,
  onClose,
}: {
  repoId: string;
  pr: PullRequest;
  onClose: () => void;
}) {
  const toast = useToast();
  const [update] = useUpdatePullRequestMutation();
  const { data: reviews, isLoading: reviewsLoading } = useGetPullRequestReviewsQuery({
    id: repoId,
    number: pr.providerNumber,
  });
  const [submitReview] = useSubmitPullRequestReviewMutation();
  const [reviewBody, setReviewBody] = useState('');
  const [reviewEvent, setReviewEvent] = useState<'APPROVE' | 'REQUEST_CHANGES' | 'COMMENT'>('COMMENT');

  const reviewList = (reviews as PullRequestReview[] | undefined) ?? [];

  const transition = (status: PullRequestStatus) => {
    update({ id: repoId, number: pr.providerNumber, body: { status } })
      .unwrap()
      .then(() => toast.success(`PR moved to ${status}`))
      .catch((err) =>
        toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Transition failed'),
      );
  };

  const submit = () => {
    if (!reviewBody.trim()) {
      toast.error('Review body is required');
      return;
    }
    submitReview({ id: repoId, number: pr.providerNumber, body: reviewBody, event: reviewEvent })
      .unwrap()
      .then(() => {
        toast.success('Review submitted');
        setReviewBody('');
      })
      .catch((err) =>
        toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Review failed'),
      );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            #{pr.providerNumber} {pr.title}
          </DialogTitle>
          <DialogDescription>
            {pr.headBranch} → {pr.baseBranch} ·{' '}
            <Badge variant={PR_STATUS_BADGE[pr.status]} className="text-[10px]">
              {pr.status}
            </Badge>
            {pr.reviewConversationId && (
              <Link
                to={`/conversations/${pr.reviewConversationId}`}
                className="ml-2 text-muted-foreground hover:text-primary"
              >
                <MessageSquare className="inline h-3 w-3" /> review pipeline
              </Link>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {pr.body && <p className="text-sm text-muted-foreground">{pr.body}</p>}

          <div className="space-y-1">
            <Label className="text-xs">Status transition</Label>
            <div className="flex flex-wrap gap-1.5">
              {(['open', 'reviewing', 'merged', 'closed'] as PullRequestStatus[]).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={pr.status === s ? 'default' : 'outline'}
                  disabled={pr.status === s}
                  onClick={() => transition(s)}
                >
                  {s}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Reviews</Label>
            {reviewsLoading ? (
              <LoadingSpinner className="py-4" />
            ) : reviewList.length === 0 ? (
              <p className="text-xs text-muted-foreground">No reviews yet.</p>
            ) : (
              <div className="space-y-1.5">
                {reviewList.map((r) => (
                  <div key={r.id} className="rounded-md border px-3 py-1.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.user ?? 'reviewer'}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {r.state ?? 'COMMENT'}
                      </Badge>
                    </div>
                    {r.body && <p className="mt-0.5 text-xs text-muted-foreground">{r.body}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2 rounded-md border p-3">
            <Label className="text-xs">Submit review</Label>
            <div className="flex gap-2">
              <select
                aria-label="Review event"
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={reviewEvent}
                onChange={(e) => setReviewEvent(e.target.value as typeof reviewEvent)}
              >
                <option value="COMMENT">Comment</option>
                <option value="APPROVE">Approve</option>
                <option value="REQUEST_CHANGES">Request changes</option>
              </select>
            </div>
            <textarea
              aria-label="Review body"
              rows={3}
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={reviewBody}
              onChange={(e) => setReviewBody(e.target.value)}
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={submit}>
                Submit review
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
